import { Link } from "react-router-dom";
import { Check, Droplets, Lightbulb, Sparkles } from "lucide-react";
import { IMAGES } from "@/lib/site";
import BeforeAfterSlider from "@/components/BeforeAfterSlider";
import useDocumentMeta from "@/hooks/useDocumentMeta";

function BeforeAfter({ before, after, label }) {
  return (
    <BeforeAfterSlider
      beforeSrc={before}
      afterSrc={after}
      beforeAlt={`${label} before`}
      afterAlt={`${label} after`}
      height="h-72 sm:h-96"
      testid={`ba-${label}`}
    />
  );
}

function ServiceBlock({ id, icon: Icon, accent, eyebrow, title, price, bullets, before, after, reversed }) {
  return (
    <section id={id} className="py-16 sm:py-20" data-testid={`service-${id}`}>
      <div className={`max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 grid lg:grid-cols-2 gap-10 items-center ${reversed ? "lg:[&>div:first-child]:order-2" : ""}`}>
        <div>
          <div className="inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-medium tracking-wide" style={{ background: accent.bg, color: accent.fg }}>
            <Icon className="w-3.5 h-3.5" /> {eyebrow}
          </div>
          <h2 className="font-heading mt-4 text-3xl sm:text-4xl font-bold text-[#111827]">{title}</h2>
          <div className="mt-3 font-heading text-2xl font-bold" style={{ color: accent.fg }}>{price}</div>
          <ul className="mt-6 space-y-3 text-[#4B5563]">
            {bullets.map((b) => (
              <li key={b} className="flex items-start gap-2"><Check className="w-4 h-4 mt-1 flex-shrink-0" style={{ color: accent.fg }} />{b}</li>
            ))}
          </ul>
          <div className="mt-7 flex flex-wrap gap-3">
            <Link to="/free-quote" className="pill-btn inline-flex rounded-full bg-[#0369A1] hover:bg-[#0284C7] text-white font-medium px-5 py-2.5 text-sm" data-testid={`${id}-quote-btn`}>Get free quote</Link>
            <Link to="/booking" className="pill-btn inline-flex rounded-full bg-white border border-gray-200 hover:border-[#0369A1] text-[#111827] font-medium px-5 py-2.5 text-sm" data-testid={`${id}-book-btn`}>Book a slot</Link>
          </div>
        </div>
        <div>
          {before && after ? <BeforeAfter before={before} after={after} label={id} /> : (
            <div className="grid grid-cols-2 gap-3">
              <div className="rounded-2xl overflow-hidden ba-card bg-white">
                <img src={IMAGES.pw_after} alt="Clean driveway" className="w-full h-56 sm:h-64 object-cover" />
              </div>
              <div className="rounded-2xl overflow-hidden ba-card bg-white">
                <img src={IMAGES.led_after} alt="LED pathway" className="w-full h-56 sm:h-64 object-cover" />
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

export default function WhatWeOffer() {
  useDocumentMeta({
    title: "Services | Pressure Washing $3/sqm & LED Lighting - Rangiora NZ",
    description: "Pressure washing driveways and concrete surfaces at $3 per sqm, continuous LED pathway lighting, plus combined packages. Free callout in Rangiora; travel fee may apply for surrounding areas.",
  });
  return (
    <div>
      {/* HEADER */}
      <section className="border-b border-gray-100 bg-white">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-14 sm:py-20" data-testid="offer-header">
          <div className="text-xs uppercase tracking-widest text-[#0369A1] font-semibold">What we offer</div>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-[#111827] mt-3 max-w-3xl leading-[1.04]">
            Pressure washing &amp; LED lighting<br />done properly in <span className="glow-underline">Rangiora</span> and Surrounding Areas.
          </h1>
          <p className="mt-5 text-[#4B5563] max-w-2xl leading-relaxed">Pick one or bundle both for the best value. We focus on concrete surfaces - driveways, paths, patios - and continuous LED strips that light pathways cleanly without spotty individual glows.</p>
        </div>
      </section>

      <ServiceBlock
        id="pressure-washing"
        icon={Droplets}
        accent={{ bg: "#E0F2FE", fg: "#0369A1" }}
        eyebrow="Pressure washing in Rangiora"
        title="Concrete brought back to life."
        price="$3 per sqm"
        bullets={[
          "Driveways, pathways, patios and concrete surfaces",
          "Industrial-grade equipment, gentle on the substrate",
          "Tidy job site - we sweep, rinse, and leave it spotless",
          "Free measure-up if you'd rather not crunch numbers",
          "Free callout in Rangiora — travel fee may apply for surrounding areas",
        ]}
        before={IMAGES.pw_before}
        after={IMAGES.pw_after}
      />

      <div className="border-t border-gray-100 bg-[#F3F4F6]">
        <ServiceBlock
          id="led-lighting"
          icon={Lightbulb}
          accent={{ bg: "#FEF3C7", fg: "#D97706" }}
          eyebrow="LED lighting in Rangiora"
          title="Continuous strips. Warm, modern pathways."
          price="Contact for quote"
          bullets={[
            "Continuous LED strip lighting (no patchy single-bulb glows)",
            "Pathway, garden edging and entry highlight installs",
            "Weatherproof, low-voltage, low-power consumption",
            "Tailored runs - we measure and design for your layout",
          ]}
          before={IMAGES.led_before}
          after={IMAGES.led_after}
          reversed
        />
      </div>

      {/* COMBO */}
      <section id="package-deal" className="py-16 sm:py-20" data-testid="service-package-deal">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
          <div className="rounded-3xl overflow-hidden bg-gradient-to-br from-[#0369A1] to-[#0284C7] text-white relative grain">
            <div className="relative grid lg:grid-cols-2 gap-0">
              <div className="p-10 sm:p-14">
                <div className="inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-medium tracking-wide bg-white/15 text-white">
                  <Sparkles className="w-3.5 h-3.5" /> Combined package deal
                </div>
                <h2 className="font-heading mt-4 text-3xl sm:text-4xl font-bold leading-[1.05]">Wash + Light, in one visit.</h2>
                <p className="mt-4 text-white/85 max-w-md">Pair pressure washing with LED pathway lighting and we&apos;ll sort it in one tidy booking. One team, one timeline, one combined quote.</p>
                <ul className="mt-6 space-y-2 text-white/90">
                  <li className="flex items-start gap-2"><Check className="w-4 h-4 mt-1 text-[#F59E0B]" />Coordinated booking - no second visit</li>
                  <li className="flex items-start gap-2"><Check className="w-4 h-4 mt-1 text-[#F59E0B]" />Combined quote with package savings</li>
                  <li className="flex items-start gap-2"><Check className="w-4 h-4 mt-1 text-[#F59E0B]" />Concrete cleaned <em>before</em> lights are run - cleaner finish, better adhesion</li>
                </ul>
                <div className="mt-7 flex flex-wrap gap-3">
                  <Link to="/free-quote" data-testid="package-quote-btn" className="pill-btn inline-flex rounded-full bg-[#F59E0B] hover:bg-[#FBBF24] text-[#111827] font-semibold px-5 py-2.5 text-sm">Free combined quote</Link>
                  <Link to="/booking" data-testid="package-book-btn" className="pill-btn inline-flex rounded-full bg-white text-[#0369A1] hover:bg-gray-100 font-semibold px-5 py-2.5 text-sm">Book a slot</Link>
                </div>
              </div>
              <div className="relative min-h-[300px] lg:min-h-full">
                <img src={IMAGES.hero} alt="Combined wash and lighting result at dusk" className="absolute inset-0 w-full h-full object-cover" />
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
